import type { CameraRig } from '../camera/cameraRig';
import type { GameContext } from '../core/context';
import type { V2 } from '../core/math';
import type { System } from '../core/system';
import type { PlayerService } from '../player/playerSystem';
import type { TimeOfDayService } from '../render/mood';
import type { VehicleService } from '../vehicles/vehicleSystem';
import { Ambience, AMBIENT_EVENTS, audioHour, type AmbientEvent } from './ambience';
import type { AudioService, BusName, Stinger, UiSound } from './audioService';
import { EnginePool } from './engine';
import { DIALOGUE_DUCK, environmentFor, heatDuck, otherEngines, sirenDuck, voiceBudget } from './mix';
import { Mixer } from './mixer';
import { OneShots } from './oneShots';
import { Radio } from './radio/radio';
import { NewsDesk } from './radio/newsDesk';
import { wireNewsDesk } from './radio/newsWire';
import { SfxBank } from './sfxBank';
import { SFX_NAMES, type SfxName } from './sfxPresets';
import { SirenPool } from './siren';
import { FirePool } from './fire';
import { createListener } from './spatial';
import { STINGER_DURATION, stingerDuck, UI_SOUNDS, UiSounds } from './uiSounds';
import { YardAmbience } from './yardAmbience';
import { failStamp } from '../ui/format';
import { WEAPONS } from '../weapons/weaponData';
import { weaponFromWire } from '../../shared/weapons';
import { weatherState } from '../fx/ambientFx';
import { DISTRICTS, districtAt, POIS } from '../world/lowBorough/data';
import type { CharacterFootstep } from '../render/characters/footsteps';
import { impactSoundFor, surfaceForDistrict, type BlowSound, type Surface } from './oneShots';
import { sceneOverride } from '../render/sceneOverride';
import { qualityTier } from '../render/qualityTier';

/**
 * Audio system (order 650, id 'audio'): provides the `audio` service, listens to game events and
 * drives engines / sirens / ambience / radio / footsteps / horn each frame. Everything is synthesised.
 * Without an AudioContext it only keeps the trigger log, so tests can still assert the wiring.
 */

const STINGERS: readonly Stinger[] = ['missionStart', 'missionSuccess', 'missionFail', 'busted', 'wasted', 'heatUp', 'heatDown', 'toast', 'districtCard'];
const AMBIENT: readonly AmbientEvent[] = AMBIENT_EVENTS;
const RECENT_MAX = 8;
/** Max collision / prop sounds per rendered frame. */
const MAX_IMPACTS_PER_FRAME = 3;
const MAX_PROPS_PER_FRAME = 4;
/** Other characters' footsteps are heard within this range, at most this many per second. */
const STEP_RANGE = 12;
const MAX_OTHER_STEPS_PER_SEC = 6;
/** The lock-up's radio through its wall (profile page): lowpass Hz and level. */
const YARD_RADIO = { cutoff: 650, level: 0.45 };
/** Pubs/clubs are audible from this far. */
const PUB_RANGE = 38;
const PUBS = POIS.filter((p) => p.kind === 'pub' || p.kind === 'club');
/** Weather changes glide into the footsteps and engines over a few seconds (the beds have their own fades). */
const WET_TC = 3;
/** Other traffic tooting at you: heard within this range, at most one per this many seconds. */
const TOOT_RANGE = 45;
const TOOT_REST = 1.4;

/** Traffic reactions that sound a horn (a car pulling over for a siren stays quiet). */
export function tootFor(kind: string): 'single' | 'double' | null {
  return kind === 'swerve' || kind === 'panic' ? 'double' : kind === 'stuck' || kind === 'bypass' ? 'single' : null;
}

/** 0 dry, 0.5 drizzle, 1 rain. */
export function wetness(w: string): number {
  return w === 'rain' ? 1 : w === 'drizzle' ? 0.5 : 0;
}

/** Melee impact sound for a weapon id and target (bats tock, blows on a car body sound like a panel). */
export function blowSoundFor(weaponId: string, base: BlowSound, target: string, firearm: boolean): BlowSound {
  if (target === 'vehicle') return firearm ? 'clang' : 'panel';
  if (weaponId === 'bat') return 'wood';
  return base;
}

export function createAudioSystem(): System {
  let ctx: GameContext;
  const mx = new Mixer();
  const listener = createListener();
  const ui = new UiSounds(mx);
  const shots = new OneShots(mx, listener);
  const bank = new SfxBank(mx, shots);
  const engines = new EnginePool(mx, listener);
  const sirens = new SirenPool(mx, listener);
  const fires = new FirePool(mx, listener);
  const ambience = new Ambience(mx);
  /** The profile page's yard (yardAmbience.ts): its dog and siren are the city's own events. */
  const yard = new YardAmbience(mx, (e) => trigger(`ambient:${e}`, () => ambience.play(e)));
  let yardOn = false;
  let radio!: Radio;
  /** The hideout's jukebox: the station index it plays (-1: none), and the car radio's station before it. */
  let jukebox = -1;
  let jukeboxBefore = -1;
  /** The radio's news desk: the player's own doings as bulletins (radio/newsDesk.ts). */
  const desk = new NewsDesk();

  let vehicles: VehicleService | undefined;
  let player: PlayerService | undefined;
  let rig: CameraRig | undefined;
  const offs: (() => void)[] = [];
  const recent: string[] = [];
  let impactsThisFrame = 0;
  let propsThisFrame = 0;
  let glassCooldown = 0;
  let coinCooldown = 0;
  let whistleCooldown = 0;
  let tootCooldown = 0;
  let pvpGruntCooldown = 0;
  let tier = '';
  /** A weapon change waits a frame: a pickup / confiscation in the same frame plays its own sound instead. */
  let swapPending = false;
  /** The aim lock last frame ('kind:id', '' none): a new one clicks. */
  let lockKey = '';
  let lockCooldown = 0;
  let hornHeld = false;
  let hornForce = 0;
  let warned = false;
  const origin: V2 = { x: 0, z: 0 };
  let district = '';
  let districtTraffic = 0.5;
  let surface: Surface = 'pavement';
  let districtClock = 0;
  let pubNear = 0;
  let wet = 0;
  let actionClock = 0;
  let stepsOff: (() => void) | null = null;
  let tod: TimeOfDayService | undefined;
  let hour = 12;
  let otherSteps = 0;
  let stepsWindow = 0;
  const stepPos: V2 = { x: 0, z: 0 };

  /** A planted foot from the animation bus: the player's own, or a nearby ped/cop (rate-limited). */
  function onStep(s: Readonly<CharacterFootstep>): void {
    if (!mx.running || ctx.state.mode !== 'playing') return;
    if (s.source === 'player') {
      shots.footstep(null, s.intensity, surface, wet);
      return;
    }
    const dx = s.x - listener.x;
    const dz = s.z - listener.z;
    if (dx * dx + dz * dz > STEP_RANGE * STEP_RANGE || otherSteps >= MAX_OTHER_STEPS_PER_SEC) return;
    otherSteps++;
    stepPos.x = s.x;
    stepPos.z = s.z;
    shots.footstep(stepPos, s.intensity, surface, wet, 0.45);
  }

  /** Record a trigger (always) and run the sound (only when the context is running). */
  function trigger(name: string, play: () => void): void {
    recent.push(name);
    if (recent.length > RECENT_MAX) recent.shift();
    if (!mx.available || !mx.running) return;
    try {
      play();
    } catch (e) {
      if (!warned && typeof console !== 'undefined') {
        warned = true;
        console.warn(`[audio] '${name}' failed`, e);
      }
    }
  }

  function playStinger(s: Stinger): void {
    ui.stinger(s);
    mx.duck(stingerDuck(s), STINGER_DURATION[s]);
  }

  function vehiclePos(id: number): V2 | null {
    return vehicles?.get(id)?.pos ?? null;
  }

  const service: AudioService = {
    get available() {
      return mx.available;
    },
    get unlocked() {
      return mx.running;
    },
    unlock() {
      mx.unlock();
    },
    setVolume(bus: BusName, v: number) {
      mx.setVolume(bus, v);
    },
    getVolume(bus: BusName) {
      return mx.volumes[bus];
    },
    ui(sound: UiSound) {
      trigger(`ui:${sound}`, () => ui.ui(sound));
    },
    sfx(name: SfxName, pos: V2 | null = null) {
      trigger(`sfx:${name}`, () => bank.play(name, pos));
    },
    stinger(s: Stinger) {
      trigger(`stinger:${s}`, () => playStinger(s));
    },
    duck(amount: number, seconds: number) {
      mx.duck(amount, seconds);
    },
    get radio() {
      return radio;
    },
    jukebox(station: string | null) {
      const i = station === null ? -1 : radio.stations.findIndex((s) => s.id === station);
      if (i < 0) {
        if (jukebox >= 0 && radio.current !== jukeboxBefore) radio.tune(jukeboxBefore);
        jukebox = -1;
        return;
      }
      if (jukebox < 0) jukeboxBefore = radio.current;
      jukebox = i;
      if (radio.current !== i) radio.tune(i);
    },
    yard(on: boolean) {
      if (on === yardOn) return;
      yardOn = on;
      if (on) {
        yard.start();
        // the radio inside the lock-up: easy listening through a brick wall
        service.jukebox('easy');
        mx.setMusicMuffle(YARD_RADIO.cutoff, YARD_RADIO.level);
      } else {
        yard.stop();
        service.jukebox(null);
        mx.setMusicMuffle(null);
      }
    },
  };

  function audioState(): Record<string, unknown> {
    return {
      available: mx.available,
      unlocked: mx.running,
      contextState: mx.contextState,
      volumes: { master: mx.volumes.master, music: mx.volumes.music, sfx: mx.volumes.sfx },
      activeVoices: mx.voiceCount + engines.activeCount + sirens.activeCount + fires.activeCount,
      limiterDb: Math.round(mx.limiterReduction * 10) / 10,
      environment: mx.environment,
      listener: { x: Math.round(listener.x * 10) / 10, z: Math.round(listener.z * 10) / 10 },
      sirenLevel: Math.round(sirens.loudest * 1000) / 1000,
      district,
      districtBlend: ambience.blend,
      hour: Math.round(hour * 100) / 100,
      surface,
      wet,
      radio: { current: radio.current, name: radio.current >= 0 ? (radio.stations[radio.current]?.name ?? '') : '', nowPlaying: radio.nowPlaying },
      engineVoices: engines.describe(),
      sirenVoices: sirens.activeCount,
      lastAmbient: ambience.lastEvent,
      yard: yardOn ? { active: yard.active, lastEvent: yard.lastEvent } : null,
      recent: recent.slice(),
    };
  }

  /** Hook entry: UI sound, stinger, or a named world one-shot played at the listener. */
  function playSound(name: string): void {
    if ((UI_SOUNDS as readonly string[]).includes(name)) return service.ui(name as UiSound);
    if ((STINGERS as readonly string[]).includes(name)) return service.stinger(name as Stinger);
    if ((SFX_NAMES as readonly string[]).includes(name)) return service.sfx(name as SfxName);
    const here: V2 = { x: listener.x + 4, z: listener.z };
    switch (name) {
      case 'impact':
      case 'crash':
        return trigger('impact', () => shots.impact(12, 9000, here));
      case 'prop':
      case 'clank':
        return trigger('prop:bin', () => shots.propHit('bin', 7, here));
      case 'pedHit':
      case 'thud':
        return trigger('pedHit', () => shots.pedHit(here, 8));
      case 'boom':
      case 'explosion':
        return trigger('boom', () => shots.boom(true, here));
      case 'destroyed':
        return trigger('destroyed', () => shots.boom(false, here));
      case 'glass':
        return trigger('glass', () => shots.glass(here));
      case 'door':
        return trigger('door', () => shots.door(null));
      case 'register':
      case 'cha-ching':
        return trigger('register', () => shots.cashRegister());
      case 'clink':
        return trigger('clink', () => shots.clink(null));
      case 'grunt':
        return trigger('grunt', () => shots.grunt(15));
      case 'horn':
        return trigger('horn', () => {
          hornForce = 0.35;
        });
      case 'footstep':
        return trigger('footstep', () => shots.footstep(null, 0.5, surface, wet));
      case 'whistle':
        return trigger('whistle', () => shots.whistle(here, [0.16, 0.55]));
      case 'toot':
        return trigger('toot', () => shots.toot(here, 1, true));
      case 'bodyFall':
        return trigger('bodyFall', () => shots.bodyFall(here));
      default:
        if (name.startsWith('ambient:') && (AMBIENT as readonly string[]).includes(name.slice(8))) {
          const e = name.slice(8) as AmbientEvent;
          return trigger(`ambient:${e}`, () => ambience.play(e));
        }
        recent.push(`unknown:${name}`);
        if (recent.length > RECENT_MAX) recent.shift();
    }
  }

  function wireEvents(): void {
    const ev = ctx.events;
    offs.push(
      ev.on('collision', (e) => {
        const a = e.a;
        const b = e.b;
        const aCar = a.kind === 'vehicle' || a.kind === 'player';
        const bCar = b !== null && (b.kind === 'vehicle' || b.kind === 'player');
        if (!aCar && !bCar) return; // ped/prop/static only: handled by their own events (or trivial)
        if (a.kind === 'ped' || a.kind === 'prop' || b?.kind === 'ped' || b?.kind === 'prop') return;
        if (e.speed < 1.5 || (!e.isNew && e.speed < 3)) return;
        if (impactsThisFrame >= MAX_IMPACTS_PER_FRAME) return;
        impactsThisFrame++;
        trigger('impact', () => shots.impact(e.speed, e.impulse, e.pos));
      }),
      ev.on('propHit', (e) => {
        if (propsThisFrame >= MAX_PROPS_PER_FRAME) return;
        propsThisFrame++;
        trigger(`prop:${e.kind}`, () => shots.propHit(e.kind, e.speed, e.pos));
      }),
      ev.on('pedHit', (e) => trigger('pedHit', () => shots.pedHit(e.pos, e.speed))),
      ev.on('vehicleExploded', (e) => trigger('boom', () => shots.boom(true, e.pos))),
      ev.on('vehicleDestroyed', (e) => {
        trigger('destroyed', () => shots.boom(false, e.pos));
        // the fire catching: a whoosh over the small boom
        trigger('sfx:fireWhoosh', () => bank.play('fireWhoosh', e.pos));
      }),
      ev.on('vehicleDamaged', (e) => {
        if (e.amount < 10 || glassCooldown > 0) return;
        const p = vehiclePos(e.vehicleId);
        if (!p) return;
        glassCooldown = 0.3;
        trigger('glass', () => shots.glass(p));
      }),
      ev.on('vehicleEntered', (e) => {
        const p = e.byPlayer ? null : vehiclePos(e.vehicleId);
        if (!e.byPlayer && !p) return;
        trigger('door', () => shots.door(p));
      }),
      ev.on('vehicleExited', (e) => {
        const p = e.byPlayer ? null : vehiclePos(e.vehicleId);
        if (!e.byPlayer && !p) return;
        trigger('door', () => shots.door(p));
      }),
      ev.on('moneyChanged', (e) => {
        if (e.amount > 0 && e.delta >= 20) trigger('register', () => shots.cashRegister());
        else if (e.delta > 0 && coinCooldown <= 0) {
          coinCooldown = 0.35;
          service.sfx('coins');
        }
      }),
      // A driver dragged out (carjack) or bailing: the body on the cobbles.
      ev.on('driverEjected', (e) => trigger('bodyFall', () => shots.bodyFall(e.pos))),
      // Traffic swerving round you / stuck behind you: a toot from that car.
      ev.on('trafficReaction', (e) => {
        const kind = tootFor(e.kind);
        if (!kind || tootCooldown > 0 || Math.hypot(e.pos.x - listener.x, e.pos.z - listener.z) > TOOT_RANGE) return;
        // Rerouting round a blocked junction happens a lot in a jam: only some drivers lean on the horn.
        if (e.kind === 'bypass' && Math.random() < 0.5) return;
        tootCooldown = TOOT_REST;
        const pitch = 0.85 + ((e.vehicleId * 7919) % 30) / 100;
        trigger('toot', () => shots.toot(e.pos, pitch, kind === 'double'));
      }),
      // The constable's pea whistle: one short blast at a warning, short-long when the chase is on.
      ev.on('policeWarning', (e) => {
        if (whistleCooldown > 0) return;
        whistleCooldown = 3;
        const a = Math.random() * Math.PI * 2;
        const at = { x: e.pos.x + Math.cos(a) * 9, z: e.pos.z + Math.sin(a) * 9 };
        trigger('whistle', () => shots.whistle(at, [0.32]));
      }),
      ev.on('policeSpotted', (e) => {
        if (whistleCooldown > 0) return;
        whistleCooldown = 6;
        const a = Math.random() * Math.PI * 2;
        const at = { x: e.pos.x + Math.cos(a) * 12, z: e.pos.z + Math.sin(a) * 12 };
        trigger('whistle', () => shots.whistle(at, [0.16, 0.55]));
      }),
      ev.on('armedResponse', (e) => {
        if (e.stage === 'authorised') service.sfx('radioSquelch');
      }),
      // Hurt by another player online (no playerHurt for that).
      ev.on('pvpHurt', () => {
        if (pvpGruntCooldown > 0) return;
        pvpGruntCooldown = 0.4;
        trigger('grunt', () => shots.grunt(14));
      }),
      ev.on('vehicleStolen', (e) => {
        if (e.byPlayer && e.carjack) trigger('clink', () => shots.clink(e.pos));
      }),
      ev.on('playerHurt', (e) => trigger('grunt', () => shots.grunt(e.amount))),
      // Weapons (src/weapons) and armed police.
      ev.on('weaponFired', (e) => trigger('gunshot', () => shots.gunshot(WEAPONS[e.weapon].cls === 'shotgun' ? 'shotgun' : 'revolver', null))),
      ev.on('policeShot', (e) => trigger('gunshot:police', () => shots.gunshot('revolver', e.from))),
      // Another player's shot online (heard where it was fired).
      ev.on('netShot', (e) => trigger('gunshot:remote', () => shots.gunshot(WEAPONS[weaponFromWire(e.weapon)].cls === 'shotgun' ? 'shotgun' : 'revolver', { x: e.x, z: e.z }))),
      ev.on('weaponSwing', (e) => {
        const m = WEAPONS[e.weapon].melee;
        trigger('swing', () => shots.swing(Math.min(1, m.swingTime / 0.72)));
      }),
      ev.on('weaponHit', (e) => {
        const w = WEAPONS[e.weapon];
        // Bullets landing are heard as the shot itself (and a body falling as pedHit), plus a quiet thwack:
        // the hit marker's half you hear (docs/CONTROLS-POLISH.md).
        if (w.firearm) {
          // A round on a car is heard as the impact kit's panel thunk (`shotImpact`).
          if (e.target !== 'vehicle') service.sfx('hitMark');
          return;
        }
        const sound = blowSoundFor(e.weapon, w.melee.sound, e.target, w.firearm !== null);
        trigger(`blow:${sound}`, () => shots.blow(sound, e.pos));
      }),
      // The impact kit (src/fx/impactFx.ts): a round landing, by surface, and now and then a ricochet.
      ev.on('shotImpact', (e) => {
        const k = impactSoundFor(e.surface);
        if (!k) return;
        trigger(`impact:${k}`, () => shots.bulletHit(k, e.pos, e.pellets));
        if (e.ricochet) trigger('ricochet', () => shots.ricochet(e.pos));
      }),
      ev.on('weaponReloadStarted', (e) => {
        const f = WEAPONS[e.weapon].firearm;
        if (f) trigger('reload', () => shots.reload(WEAPONS[e.weapon].cls === 'shotgun' ? 'shotgun' : 'revolver', f.reloadTime));
      }),
      ev.on('weaponDryFire', () => trigger('dryFire', () => shots.dryFire())),
      // ZzFX one-shots (sfxPresets.ts) for events that had no sound.
      ev.on('weaponChanged', () => {
        swapPending = ctx.state.mode === 'playing';
      }),
      ev.on('weaponPickedUp', () => {
        swapPending = false;
        service.sfx('pickupWeapon');
      }),
      ev.on('weaponsConfiscated', () => {
        swapPending = false;
      }),
      ev.on('weaponsDropped', () => {
        swapPending = false;
      }),
      ev.on('policeAim', (e) => service.sfx('hammerCock', e.pos)),
      ev.on('lampBroken', (e) => {
        trigger('glass', () => shots.glass(e.pos));
        service.sfx('lampFizz', e.pos);
      }),
      ev.on('furnitureKnocked', (e) => service.sfx('poleClang', e.pos)),
      ev.on('garageWork', () => service.sfx('ratchet')),
      ev.on('sprayBayOpened', () => service.sfx('shutterUp')),
      ev.on('sprayBayClosed', () => service.sfx('shutterDown')),
      ev.on('resprayed', () => {
        service.sfx('canRattle');
        setTimeout(() => service.sfx('sprayHiss'), 260);
      }),
      ev.on('pedScream', (e) => trigger('scream', () => shots.scream(e.pos))),
      ev.on('missionStarted', () => service.stinger('missionStart')),
      ev.on('missionCompleted', () => service.stinger('missionSuccess')),
      // Nicked / wasted mid-job: the NICKED / WASTED stinger (on playerOutcome, with the sheet) says it
      // all — don't stack the job-blown cue on top.
      ev.on('missionFailed', (e) => {
        if (failStamp(e.reason).kind === 'failed') service.stinger('missionFail');
      }),
      ev.on('playerOutcome', (e) => service.stinger(e.kind === 'busted' ? 'busted' : 'wasted')),
      // A line of dialogue on the HUD: the radio sits back while it shows (ambience stays).
      ev.on('dialogueShown', (e) => {
        recent.push('dialogue');
        if (recent.length > RECENT_MAX) recent.shift();
        const secs = Math.max(1, Math.min(12, e.seconds));
        mx.duckMusic(DIALOGUE_DUCK.music, secs);
        mx.duckAmbience(DIALOGUE_DUCK.ambience, secs);
      }),
      ev.on('heatChanged', (e) => {
        if (e.level > e.previous) service.stinger('heatUp');
        else if (e.level === 0 && e.previous > 0) service.stinger('heatDown');
      }),
    );
  }

  function hornPitch(): number {
    switch (player?.vehicle?.spec.id) {
      case 'lorry':
        return 0.72;
      case 'van':
        return 0.86;
      case 'compact':
      case 'roadster':
        return 1.12;
      case 'exec':
        return 0.92;
      default:
        return 1;
    }
  }

  function frame(dt: number): void {
    vehicles ??= ctx.services.tryGet('vehicles');
    player ??= ctx.services.tryGet('player');
    rig ??= ctx.services.tryGet('cameraRig');
    impactsThisFrame = 0;
    propsThisFrame = 0;
    glassCooldown = Math.max(0, glassCooldown - dt);
    coinCooldown = Math.max(0, coinCooldown - dt);
    whistleCooldown = Math.max(0, whistleCooldown - dt);
    tootCooldown = Math.max(0, tootCooldown - dt);
    pvpGruntCooldown = Math.max(0, pvpGruntCooldown - dt);
    // CPU on a phone: LOW gets a smaller one-shot budget and fewer other cars' engines.
    const q = qualityTier();
    if (q !== tier) {
      tier = q;
      mx.maxVoices = voiceBudget(q);
      engines.maxOthers = otherEngines(q);
    }
    lockCooldown = Math.max(0, lockCooldown - dt);
    if (swapPending) {
      swapPending = false;
      service.sfx('weaponSwap');
    }
    // Aim lock: a quiet click when it takes a new target.
    const lock = ctx.services.tryGet('weapons')?.lock ?? null;
    const key = lock ? `${lock.kind}:${lock.id}` : '';
    if (key && key !== lockKey && lockCooldown <= 0 && ctx.state.mode === 'playing') {
      lockCooldown = 0.3;
      service.sfx('lockOn');
    }
    lockKey = key;

    // Listener = what the camera looks at; velocity for Doppler.
    const focus = rig?.lookAt ?? player?.pos ?? origin;
    listener.x = focus.x;
    listener.z = focus.z;
    if (rig) {
      const r = rig.screenRight();
      listener.rx = r.x;
      listener.rz = r.z;
    }
    const lv = player ? player.velocity : origin;
    listener.vx = lv.x;
    listener.vz = lv.z;

    const mode = player?.mode ?? 'foot';
    const driving = mode === 'driving';
    const paused = ctx.state.mode === 'paused';
    const speed = Math.hypot(lv.x, lv.z);
    mx.setPaused(paused);

    // Where we are (district colour, ground underfoot), the weather, the nearest open pub.
    const wetTarget = wetness(weatherState.current);
    wet += (wetTarget - wet) * Math.min(1, dt / WET_TC);
    if (Math.abs(wetTarget - wet) < 0.01) wet = wetTarget;
    districtClock -= dt;
    if (districtClock <= 0) {
      districtClock = 0.5;
      district = districtAt({ x: listener.x, z: listener.z });
      districtTraffic = DISTRICTS.find((d) => d.id === district)?.trafficDensity ?? 0.5;
      surface = surfaceForDistrict(district);
      let best = Infinity;
      for (const p of PUBS) best = Math.min(best, Math.hypot(p.pos.x - listener.x, p.pos.z - listener.z));
      pubNear = Math.max(0, 1 - best / PUB_RANGE);
    }
    stepsWindow += dt;
    if (stepsWindow >= 1) {
      stepsWindow = 0;
      otherSteps = 0;
    }
    if (!stepsOff) {
      const bus = ctx.services.tryGet('characterFootsteps');
      if (bus) stepsOff = bus.subscribe(onStep);
    }

    engines.wet = wet;
    engines.update(dt, vehicles, player);
    sirens.update(dt, vehicles);
    fires.update(dt, vehicles);
    // The hour the soundscape follows: the borough clock, or the held Settings -> Time of day mood.
    tod ??= ctx.services.tryGet('timeOfDay');
    hour = audioHour(tod, ctx.state.timeOfDay);
    ambience.update(dt, { hour, driving, speed, district, traffic: districtTraffic, wet, pub: pubNear, pos: listener });
    yard.update(dt);

    // The street, the car or a room (an interior drawn instead of the city: hideout, Sid's back room,
    // the spray studio): the ambience and other cars glide through the environment's filter.
    mx.setEnvironment(environmentFor(driving, sceneOverride.scene !== null));
    // The radio and ambience sit back under a siren as it closes in, and come back as it passes.
    const sd = sirenDuck(paused ? 0 : sirens.loudest);
    mx.setSirenDuck(sd.music, sd.ambience);

    // Action ducking: the radio sits back during a pursuit.
    actionClock -= dt;
    if (actionClock <= 0) {
      actionClock = 0.5;
      const hd = heatDuck(ctx.state.heat);
      if (hd > 0 && !paused) mx.duckMusic(hd, 0.9);
    }

    if (driving && ctx.input.framePressed('radio')) radio.next();
    // The radio plays on under the pause menu (the mixer ducks it); only leaving the car closes the gate —
    // or a jukebox indoors (the hideout) holds it open on foot.
    radio.update(dt, driving || jukebox >= 0);

    const horn = driving && !paused && ctx.input.held('horn');
    if (horn && !hornHeld) {
      recent.push('horn');
      if (recent.length > RECENT_MAX) recent.shift();
    }
    hornHeld = horn;
    hornForce = Math.max(0, hornForce - dt);
    shots.setHorn(horn || hornForce > 0, hornPitch());

    // Fallback cadence when no view publishes footsteps (headless / no animation bus).
    if (!stepsOff) shots.footsteps(dt, mode === 'foot' && !paused && speed > 0.5, player?.running ?? false, surface, wet);
  }

  return {
    id: 'audio',
    order: 650,
    init(c) {
      ctx = c;
      radio = new Radio(mx, (index) => {
        const st = index >= 0 ? radio.stations[index] : undefined;
        recent.push(`radio:${index}`);
        if (recent.length > RECENT_MAX) recent.shift();
        ctx.events.emit('radioChanged', { index, name: st?.name ?? '', tagline: st?.tagline ?? '' });
      });
      radio.bulletins = (slot) => desk.take(slot, ctx.state.time);
      offs.push(wireNewsDesk(c, desk));
      c.services.provide('audio', service);
      c.hooks.register('newsDesk', () => ({ pending: desk.pending(ctx.state.time), bulletin: radio.bulletin }));
      c.hooks.register('audioState', audioState);
      c.hooks.register('playSound', playSound);
      c.hooks.registerSnapshot('audio', audioState);
      wireEvents();
      mx.attachGestureListeners();
    },
    start() {
      vehicles = ctx.services.tryGet('vehicles');
      player = ctx.services.tryGet('player');
      rig = ctx.services.tryGet('cameraRig');
    },
    update(dt) {
      try {
        frame(dt);
      } catch (e) {
        if (!warned && typeof console !== 'undefined') {
          warned = true;
          console.warn('[audio] frame failed', e);
        }
      }
    },
    dispose() {
      for (const off of offs.splice(0)) off();
      stepsOff?.();
      stepsOff = null;
      shots.dispose();
      engines.dispose();
      sirens.dispose();
      fires.dispose();
      ambience.dispose();
      yard.dispose();
      radio.dispose();
      mx.dispose();
    },
  };
}
